import { type BlockDisplayed, EVERY_BLOCK_SHOWN, tempsMasked } from '@/components/reader';
import { edgeInThread } from '@/lib/experience-draft';
import type { ExperiencePageDraft } from '@matter_extractor/shared';
import { conversationRunOf, playablePages } from '@matter_extractor/shared';

/**
 * LA MARCHE de l'aperçu plein écran, pure (refonte modulaire §5.3, §9.2, §9.3 ;
 * issue #141).
 *
 * Where is one in the experience, and what do the two arrows do. All of it
 * pure, none of it inside a component, for the reason `reader-play.ts` gives
 * for its own arithmetic: a walk is exactly the thing a reviewer wants to read
 * without a DOM, and it is the half of this lot that can be wrong in a way that
 * looks fine on screen.
 *
 * ## Elle est LINÉAIRE, et rien ici n'en décide
 *
 * « Une expérience est une ligne : pas de branchement » (§9.1). A Bouton makes
 * `next-page` or `next-temps` and never chooses a destination, so the order the
 * pages are stored in IS the order they are played in. `playablePages` of
 * `shared` already says which pages are served at all - an empty protocol page
 * is skipped (§9.5) - and this module walks that list and nothing else. There
 * is no second answer to « quelle est la page suivante » here, which is the one
 * thing that would have made the preview lie about the session.
 *
 * ## Pourquoi `←` ramène la page précédente ENTIÈRE
 *
 * The session has no back (`session-player.tsx`: « le lecteur avance
 * seulement »), so the way back is the preview's own and it had to be chosen.
 * Showing the previous page whole rather than at its first temps buys the
 * replay for free: `←` then `→` lands on the page one was leaving, which
 * remounts it and plays it again from its first temps - « une page reprise
 * rejoue ses temps d'apparition depuis le début » (§5.3), obtained without a
 * replay control anybody has to find.
 *
 * ## Une page du fil n'a pas de temps (prolongation.md §3.3, issue #186)
 *
 * Dans le fil, « tout ce qui se clique arrive d'un coup sous la bulle » : les
 * temps de la page sont ignorés. La marche la tient donc ENTIÈRE dès qu'on y
 * arrive (`revealed` à son plein), `→` la franchit sans rien révéler et `←`
 * ramène la page d'avant sans « masquer un temps » que personne ne voit. Qui est
 * « dans le fil » se lit sur la liste que la marche parcourt - la suite de pages
 * qui suit directement la page protocole JOUABLE (`conversationRunOf`) : une page
 * cochée dont la page protocole est sautée se joue comme une page ordinaire
 * (§9), et la marche la traite comme telle.
 *
 * ## Un temps masqué ne se révèle pas (§9.2, H70)
 *
 * The display conditions are evaluated on what the person looking has typed
 * and chosen (§9.7), and a temps EVERY block of which is masked is « sauté, son
 * délai compris ». So the arrows step over it: `→` goes to the next temps that
 * shows something - or to the next page when none is left - and `←` hides the
 * last temps that showed something. An arrow that revealed an empty temps would
 * look like an arrow that did nothing. `displayed` defaults to every block, so
 * a walk that knows of no condition is the walk of issue #141.
 */

/**
 * Where the walk stands: which PLAYABLE page, and how many of its temps show.
 *
 * `revealed` is counted the way the reader counts it (`PageReaderProps.revealed`):
 * 1 is « the first temps is on screen », and 0 is a page that holds no temps at
 * all - the protocol page, and a content page nobody has filled yet.
 */
export type PreviewStep = {
  readonly pageIndex: number;
  readonly revealed: number;
};

/** The pages the preview walks - the session's own list, and for its reasons (§9.5). */
export function previewPages(pages: readonly ExperiencePageDraft[]): ExperiencePageDraft[] {
  return playablePages(pages);
}

/** How many temps a page shows when it shows ENTIRE. */
function tempsCount(page: ExperiencePageDraft | undefined): number {
  return page?.temps.length ?? 0;
}

/** Whether the walk plays the page IN THE THREAD - see the head: its temps are ignored. */
export function playsInThread(pages: readonly ExperiencePageDraft[], pageIndex: number): boolean {
  const page = pages[pageIndex];
  return page !== undefined && conversationRunOf(pages).includes(page.id);
}

/** A page at its beginning: its first temps, or nothing at all when it has none. */
function firstStep(pages: readonly ExperiencePageDraft[], pageIndex: number): PreviewStep {
  const count = tempsCount(pages[pageIndex]);
  // A page of the thread shows ALL it holds at once: its temps are ignored.
  return { pageIndex, revealed: playsInThread(pages, pageIndex) ? count : Math.min(1, count) };
}

/**
 * Where the preview OPENS: on the page one was looking at (§9.1).
 *
 * The page being edited, or the one the Timeline's rail has selected. A page
 * the playable list does not hold - an empty protocol page, which §9.5 skips -
 * falls back to the beginning rather than to nothing: `←` walks back to the
 * start anyway, so opening one page early costs nothing and opening on a blank
 * screen would cost the whole gesture.
 */
export function previewStart(
  pages: readonly ExperiencePageDraft[],
  pageId: string | null,
): PreviewStep {
  const index = pages.findIndex((page) => page.id === pageId);
  return firstStep(pages, index === -1 ? 0 : index);
}

/**
 * `→` : un temps de plus, TOUT DE SUITE (issue #141).
 *
 * The delay of §9.3 is ignored, a temps waiting for a click (H30) is ignored,
 * and a required field that greys the page's button is ignored too - none of
 * the three is consulted, because none of them is read here: the step says how
 * many temps show and the reader obeys. That is the whole reason the count was
 * made controllable. One must be able to cross an experience without filling
 * it in, which is what an aperçu is for.
 *
 * Every temps shown, it is the next page's turn. On the last page fully shown
 * it answers the step it was given: the last page IS the end (§5.3), and the
 * preview adds no screen behind it any more than the session does.
 */
export function previewForward(
  pages: readonly ExperiencePageDraft[],
  step: PreviewStep,
  displayed: BlockDisplayed = EVERY_BLOCK_SHOWN,
): PreviewStep {
  const page = pages[step.pageIndex];
  if (page === undefined) return step;
  const ignoresTemps = playsInThread(pages, step.pageIndex);
  for (
    let revealed = step.revealed + 1;
    !ignoresTemps && revealed <= page.temps.length;
    revealed += 1
  ) {
    const temps = page.temps[revealed - 1];
    if (temps !== undefined && !tempsMasked(temps, displayed)) return { ...step, revealed };
  }
  return previewCrossPage(pages, step);
}

/**
 * `←` : le dernier temps révélé se masque ; au premier, la page précédente
 * ENTIÈRE (see the head).
 *
 * `revealed` at 0 - a page with no temps, the protocol page - is « at the first
 * temps » too: there is nothing to hide, so the arrow crosses. On the first
 * page it answers the step it was given, because there is nothing before it.
 */
export function previewBackward(
  pages: readonly ExperiencePageDraft[],
  step: PreviewStep,
  displayed: BlockDisplayed = EVERY_BLOCK_SHOWN,
): PreviewStep {
  const showing = (pages[step.pageIndex]?.temps ?? [])
    .slice(0, Math.max(0, step.revealed))
    .map((temps) => !tempsMasked(temps, displayed));
  if (!playsInThread(pages, step.pageIndex) && showing.filter(Boolean).length > 1) {
    // Hide the last temps that SHOWS something, and the masked ones after it.
    return { ...step, revealed: showing.lastIndexOf(true) };
  }
  const previous = step.pageIndex - 1;
  if (previous < 0) return step;
  return { pageIndex: previous, revealed: tempsCount(pages[previous]) };
}

/**
 * La page suivante, à son début : ce qu'un Bouton `next-page` et une durée
 * automatique font (§9.2).
 *
 * The same answer `→` gives on a page whose temps have all shown, and
 * deliberately one function: a Bouton pressed in the preview must land exactly
 * where the arrow would have landed, or the two ways of walking would be two
 * experiences.
 */
export function previewCrossPage(
  pages: readonly ExperiencePageDraft[],
  step: PreviewStep,
): PreviewStep {
  const next = step.pageIndex + 1;
  return next < pages.length ? firstStep(pages, next) : step;
}

/** The page the step stands on, or null on an experience with no playable page. */
export function previewPageAt(
  pages: readonly ExperiencePageDraft[],
  step: PreviewStep,
): ExperiencePageDraft | null {
  return pages[step.pageIndex] ?? null;
}

/**
 * The transition to play ON ARRIVAL, carried by the edge LEAVING the previous
 * page (§9.3) - the rule `session-player.tsx` reads the same way.
 */
export function previewEnterTransition(
  pages: readonly ExperiencePageDraft[],
  step: PreviewStep,
): ExperiencePageDraft['transition'] | null {
  // Between two pages of the thread, and from the protocol page to the first one,
  // no transition is played: the chat stays on screen (prolongation.md §3.3).
  if (edgeInThread(pages, step.pageIndex)) return null;
  return step.pageIndex > 0 ? (pages[step.pageIndex - 1]?.transition ?? null) : null;
}
